import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { Select } from 'primeng/select';
import { CentralizedDepartmentService } from './centralized-department.service';
import { Observable, catchError, finalize, of } from 'rxjs';
import { ConflictErrorMessage, GeneralErrorMessage } from '@core/constants/error-messages.const';

@Component({
    selector: 'app-centralized-department-toggle',
    templateUrl: './centralized-department-toggle.component.html',
    styleUrls: ['./centralized-department-toggle.component.scss'],
    standalone: false
})
export class CentralizedDepartmentToggleComponent implements OnChanges {
  @Input() userId!: string;
  @Input() currentCompany!: string | null;
  @Input() accountType!: string;

  public isLoading: boolean = false;
  public isToggleDisabled: boolean = false;
  public companiesOptions: SelectItem[] = [];
  public isCentralizedDepartmentControl = new FormControl(false);
  public companyControl = new FormControl<string | null>(null);


  @ViewChild('companyDropdown', { static: false }) companyDropdown?: Select;

  constructor(
    private readonly centralizedDepartmentService: CentralizedDepartmentService,
    private readonly messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { accountType, currentCompany } = changes;

    if (accountType && !accountType.firstChange) {
      this.onAccountTypeChange();
    }

    if (currentCompany) {
      this.getCompaniesList();
    }
  }

  toggleSwitchChanged() {
    console.log('toggle switch changed');
    if (!this.isCentralizedDepartmentControl.value) {
      this.unsetCompany();
      this.resetCompanyControl();
      return;
    }
    this.focusCompanyDropdown();
  }

  onCompanyControlBlur() {
    this.companyControl.setValidators([Validators.required]);
    this.companyControl.markAsDirty();
    this.companyControl.updateValueAndValidity();
  }

  setCompany() {
    if (!this.companyControl.value) {
      return;
    }
    this.centralizedDepartmentService
      .setCompany(this.userId, this.companyControl.value)
      .pipe(catchError(error => this.showErrorMessage(error.status)))
      .subscribe(res => {
        if (res) this.currentCompany = this.companyControl.value;
      });
  }

  private unsetCompany() {
    if (!this.currentCompany) {
      return;
    }
    this.centralizedDepartmentService
      .unsetCompany(this.userId, this.currentCompany)
      .pipe(catchError(error => this.showErrorMessage(error.status)))
      .subscribe(res => {
        if (res) this.currentCompany = null;
      });
  }

  private onAccountTypeChange() {
    this.isCentralizedDepartmentControl.setValue(false);
    this.toggleSwitchChanged();
  }

  private getCompaniesList() {
    this.isLoading = true;
    this.centralizedDepartmentService.getCompaniesList(this.userId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe((res) => this.setCompanyData(res));
  }

  private setCompanyData(companies: string[] = []): void {
    this.isToggleDisabled = companies.length === 0;
    this.companiesOptions = companies.map((company) => ({
      label: company,
      value: company
    }));
    this.isCentralizedDepartmentControl.setValue(!!this.currentCompany);
    this.companyControl.setValue(this.currentCompany);
  }

  private focusCompanyDropdown() {
    setTimeout(() => {
      if (!this.companyDropdown?.focused) {
        this.companyDropdown?.focus();
        this.companyDropdown?.show();
      }
    });
  }

  private resetCompanyControl() {
    this.companyControl.setValue(null);
    this.companyControl.setErrors(null);
    this.companyControl.markAsUntouched();
    this.companyControl.markAsPristine();
  }

  private showErrorMessage(code: number): Observable<boolean> {
    const isConflict = code === 409;
    this.messageService.add({
      severity: isConflict ? 'warn' : 'error',
      summary: isConflict ? 'Warning' : 'Error',
      detail: isConflict ? ConflictErrorMessage : GeneralErrorMessage,
      life: 5000
    });
    return of(false);
  }
}
